import React, { useMemo, useState } from 'react';
import { Search, Calendar, Clock, MapPin, Pencil, Trash2, Eye, Plus } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Select } from './Sheet';
import Button from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { mediaUrl } from '../../services/api';
import { canCreateCell, canDeleteCell, canEditCell, canViewCell } from '../../lib/cellPermissions';
import { fullAddress } from '../../lib/utils';

const DAYS = ['Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado', 'Domingo'];

function CellCard({ cell, network, user, onEdit, onDelete, onView }) {
  const cover = cell.meetingPhotos?.[0] || cell.leaderPhotos?.[0] || cell.host?.avatar || cell.leader?.avatar;

  return (
    <article
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft"
      data-testid={`admin-cell-card-${cell.id}`}
    >
      <div className="relative h-32 w-full bg-slate-100">
        {cover ? (
          <img src={mediaUrl(cover)} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-ink-faint">
            <MapPin size={22} />
          </div>
        )}
        <Badge tone={cell.active === false ? 'neutral' : 'success'} className="absolute right-2 top-2">
          {cell.active === false ? 'Inativa' : 'Ativa'}
        </Badge>
      </div>

      <div className="p-3.5">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: network?.color || '#0284c7' }} />
          <h4 className="truncate font-heading font-bold text-ink">{cell.name}</h4>
        </div>
        <p className="mt-0.5 truncate text-xs font-medium text-ink-faint">{network?.name || '—'}</p>

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-soft">
          <span className="inline-flex items-center gap-1"><Calendar size={13} className="text-primary" /> {cell.day}</span>
          <span className="inline-flex items-center gap-1"><Clock size={13} className="text-primary" /> {cell.time}</span>
        </div>
        <p className="mt-1 truncate text-xs text-ink-soft">{cell.leader?.name}</p>
        <p className="mt-0.5 flex items-start gap-1 text-xs text-ink-faint">
          <MapPin size={13} className="mt-0.5 shrink-0" /> {fullAddress(cell.address)}
        </p>

        <div className="mt-3 flex gap-2">
          <button
            onClick={onView}
            className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full bg-slate-100 text-xs font-semibold text-ink-soft hover:text-primary"
            data-testid={`admin-cell-view-${cell.id}`}
          >
            <Eye size={14} /> Ver
          </button>
          {canEditCell(user, cell) && (
            <button
              onClick={onEdit}
              className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-full bg-primary-light text-xs font-semibold text-primary hover:bg-primary hover:text-white"
              data-testid={`admin-cell-edit-${cell.id}`}
            >
              <Pencil size={14} /> Editar
            </button>
          )}
          {canDeleteCell(user) && (
            <button
              onClick={onDelete}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-500 hover:bg-rose-100"
              aria-label="Excluir"
              data-testid={`admin-cell-delete-${cell.id}`}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export default function CellsManager({ cells, networks, loading, onEdit, onDelete, onView }) {
  const { auth } = useAuth();
  const user = auth?.user;
  const [q, setQ] = useState('');
  const [dayFilter, setDayFilter] = useState('');
  const [networkFilter, setNetworkFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const netById = useMemo(() => Object.fromEntries(networks.map((n) => [n.id, n])), [networks]);

  const visible = useMemo(
    () => cells.filter((c) => canViewCell(user, c)),
    [cells, user],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return visible.filter((c) => {
      if (dayFilter && c.day !== dayFilter) return false;
      if (networkFilter && c.networkId !== networkFilter) return false;
      if (statusFilter === 'active' && c.active === false) return false;
      if (statusFilter === 'inactive' && c.active !== false) return false;
      if (!term) return true;
      const haystack = [c.name, c.leader?.name, c.address?.neighborhood, c.address?.street].join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [visible, q, dayFilter, networkFilter, statusFilter]);

  return (
    <div>
      <div className="flex flex-col gap-2.5 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome, líder ou bairro..."
            className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 text-sm text-ink outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            data-testid="admin-cell-search"
          />
        </div>
        <div className="grid grid-cols-3 gap-2 md:flex md:w-auto">
          <Select
            testid="admin-cell-filter-day"
            value={dayFilter}
            onChange={(e) => setDayFilter(e.target.value)}
            options={[{ value: '', label: 'Todos os dias' }, ...DAYS.map((d) => ({ value: d, label: d }))]}
          />
          <Select
            testid="admin-cell-filter-network"
            value={networkFilter}
            onChange={(e) => setNetworkFilter(e.target.value)}
            options={[{ value: '', label: 'Todas as redes' }, ...networks.map((n) => ({ value: n.id, label: n.name }))]}
          />
          <Select
            testid="admin-cell-filter-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[{ value: '', label: 'Todos os status' }, { value: 'active', label: 'Ativas' }, { value: 'inactive', label: 'Inativas' }]}
          />
        </div>
      </div>

      {canCreateCell(user) && (
        <Button className="mt-3 w-full md:w-auto" onClick={() => onEdit(null)} data-testid="admin-cell-add">
          <Plus size={18} /> Nova célula
        </Button>
      )}

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {loading &&
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-72 animate-pulse rounded-2xl bg-slate-200/70" />
          ))}

        {!loading && filtered.length === 0 && (
          <p className="col-span-full py-10 text-center text-sm text-ink-soft">Nenhuma célula encontrada.</p>
        )}

        {!loading &&
          filtered.map((c) => (
            <CellCard
              key={c.id}
              cell={c}
              network={netById[c.networkId]}
              user={user}
              onView={() => onView(c)}
              onEdit={() => onEdit(c)}
              onDelete={() => onDelete(c)}
            />
          ))}
      </div>
    </div>
  );
}
